/**
 * Tab styles — THE tab design for the whole app, in one stylesheet.
 *
 * Lives here rather than as inline style objects inside Tabs.tsx so the design
 * is a single editable thing: changing how a selected tab reads is a change to
 * one rule, not to a branchy `getTabButtonStyle` function. It also buys real
 * CSS `:hover`, which removes the per-row React hover state that used to
 * re-render the whole row on every mouse move.
 *
 * ONE SHAPE, THREE SCALES, keyed off `data-depth` on the row. Every level is
 * a boxed tab raised off a baseline; deeper rows are smaller and quieter.
 *
 * This replaced a three-SHAPE system (boxed / segmented pill / bare text
 * links) that marked hierarchy well but broke the more basic contract: a pill
 * and a text link do not read as tabs, so a sub-row looked like a different
 * kind of control — or, at the leaf, like prose. Hierarchy is worth less than
 * legibility here, so size now carries it alone. The Account and Location
 * modals stack up to three rows (Financials → Invoicing → Schedules); check
 * that stack when changing the scale steps.
 *
 * WHY THE SURFACES ARE OVERLAYS. Tab rows render on the black page
 * (background.primary #000000) and on the lighter modal surface
 * (background.tertiary #1A1A1A) alike, so no opaque fill suits both: the
 * previous active-tab fill was background.secondary #0B0B0B, DARKER than the
 * modal it sat in, so the selected tab read as a hole punched into the surface
 * rather than a tab raised off it, and its "merge with the content" bottom
 * border painted a dark seam across the baseline. Every surface below is a
 * translucent white/black overlay instead, so contrast holds on any ground.
 *
 * The selected tab's brand bar sits on the BOTTOM edge, matching the app's own
 * primary nav. It used to sit on top, which read as a card and disagreed with
 * every other tab row in the product.
 */
// Resolved palette: on web these are `var(--tc-…)` strings, so the injected
// stylesheet (created once) still switches with light/dark. Importing the raw
// dark palette here baked #FFFFFF into the selected tab, turning its label and
// icon white on a light ground.
import { typography } from './typography';
import { tokenVars as t } from './tokens';
import { compactSpec, sectionSpec, textRoles } from './textRoles';

const T = sectionSpec.tabs;
const CT = compactSpec.tabs;
const tabRole = textRoles.tab;

/**
 * The chosen tab on the settings pages: a raised neutral pill, marked like the
 * top nav's open tab (`washStrong` ground, primary text at 600) plus a 1px
 * `border` edge so it still lifts off a white track in light mode, and a 2px
 * `brand` underline. Not a solid scarlet fill: the design keeps the ember for
 * actions (primary buttons, Ready to invoice). The edge and the underline are
 * inset shadows, so choosing a tab never moves the row. Shared by the
 * stylesheet below and the web ds SegmentedTabs (inside TabsKeepLabels).
 */
export const chosenTabLook = {
  background: t.washStrong,
  color: t.text.primary,
  fontWeight: 600,
  boxShadow: `inset 0 -2px 0 ${t.brand}, inset 0 0 0 1px ${t.border}, 0 1px 2px rgba(0, 0, 0, 0.08)`,
} as const;

/** Class names the stylesheet defines, exported so consumers avoid magic strings. */
export const tabClasses = {
  wrapper: 'tc-tabs',
  row: 'tc-tabs-row',
  tab: 'tc-tab',
  badge: 'tc-tab-badge',
  warning: 'tc-tab-warning',
} as const;

export const tabStyles = `
.${tabClasses.wrapper} { position: relative; }
/* Every level's baseline spans the full panel width rather than stopping
   under the last tab, so a row reads as a row at any depth. */

.${tabClasses.row} {
  display: flex;
  gap: 2px;
  border-bottom: 1px solid ${t.borderSubtle};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.${tabClasses.row}::-webkit-scrollbar { display: none; }

.${tabClasses.tab} {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: 1px solid transparent;
  color: ${t.text.muted};
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.medium};
  transition: background-color 150ms ease-out, color 150ms ease-out, border-color 150ms ease-out;
}
.${tabClasses.tab}[aria-selected="true"] {
  color: ${t.text.primary};
  font-weight: ${typography.fontWeight.semibold};
}
.${tabClasses.tab}:disabled { opacity: 0.5; cursor: not-allowed; }
.${tabClasses.tab}:focus-visible {
  outline: 2px solid ${t.focus};
  outline-offset: -2px;
}
.${tabClasses.row}[data-full-width="true"] .${tabClasses.tab} { flex: 1; justify-content: center; }

/* ---- ONE shape at every level; only the scale changes ---- */
/* Tabs look like tabs wherever they are. An earlier design gave each level a
   different SHAPE — boxed, then a segmented pill, then bare text links — to
   mark which row owned which when the Account modal stacks three of them
   (Financials → Invoicing → Schedules). It worked as a hierarchy cue and
   failed at the more basic job: a pill and a text link do not read as tabs,
   so a sub-row looked like a different kind of control, or like prose.
   Hierarchy is now carried by SIZE alone. */
.${tabClasses.row} .${tabClasses.tab} {
  border-radius: 6px 6px 0 0;
  border-bottom-width: 2px;
  margin-bottom: -1px;
}
.${tabClasses.row} .${tabClasses.tab}:hover:not([aria-selected="true"]):not(:disabled) {
  background: ${t.wash};
  color: ${t.text.primary};
}
.${tabClasses.row} .${tabClasses.tab}[aria-selected="true"] {
  background: ${t.washStrong};
  border-color: ${t.borderSubtle};
  border-bottom-color: ${t.brand};
}

/* Metrics. Depth 0 is the only level whose size the caller may pick; deeper
   rows step down on a fixed scale so a nested row is unmistakably subordinate
   to the one above it without changing shape. */
.${tabClasses.row}[data-depth="0"][data-size="small"] .${tabClasses.tab} { padding: 8px 12px; font-size: ${typography.fontSize.sm}px; }
.${tabClasses.row}[data-depth="0"][data-size="medium"] .${tabClasses.tab} { padding: 10px 16px; font-size: ${typography.fontSize.md}px; }
.${tabClasses.row}[data-depth="0"][data-size="large"] .${tabClasses.tab} { padding: 12px 20px; font-size: ${typography.fontSize.lg}px; }
.${tabClasses.row}[data-depth="1"] .${tabClasses.tab} { padding: 7px 13px; font-size: ${typography.fontSize.sm}px; }
.${tabClasses.row}[data-depth="2"] .${tabClasses.tab} { padding: 5px 10px; font-size: ${typography.fontSize.xs}px; }

.${tabClasses.badge} {
  font-size: 10px;
  line-height: 16px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  background-color: ${t.successSubtle};
  color: ${t.success};
}

/* ---- Settings pages: the design system's segmented tabs ----
   Inside TabsKeepLabels (the Account and Location settings pages) a tab row is
   the same control as the ds SegmentedTabs (apps/web/src/components/ui/ds):
   segments on a bgSubtle track with a border, the chosen one a raised
   neutral pill (chosenTabLook: washStrong, primary text, a 2px brand
   underline), never underline-only and never solid scarlet. The top row is the default
   density (44px segments, 15px labels, stretching across the row); a row
   nested in a tab is compact (36px, 44px on a phone, sized to its labels).
   Rows wrap instead of scrolling, so every label stays readable. Numbers
   come from sectionSpec / compactSpec, the same as SegmentedTabs. */
.${tabClasses.row}[data-look="segmented"] {
  flex-wrap: wrap;
  overflow: visible;
  gap: ${T.gap}px;
  padding: ${T.trackPadding}px;
  background: ${t.bgSubtle};
  border: 1px solid ${t.border};
  border-radius: ${T.trackRadius}px;
  box-sizing: border-box;
}
.${tabClasses.row}[data-look="segmented"][data-depth] .${tabClasses.tab} {
  flex: 1 1 auto;
  justify-content: center;
  min-height: ${T.minHeight}px;
  min-width: 0;
  padding: 0 14px;
  margin: 0;
  border: 0;
  border-radius: ${T.tabRadius}px;
  background: transparent;
  color: ${t.text.secondary};
  font-size: ${tabRole.fontSize}px;
  line-height: ${tabRole.lineHeight}px;
  font-weight: ${tabRole.fontWeight};
  white-space: normal;
  text-align: center;
}
.${tabClasses.row}[data-look="segmented"][data-depth] .${tabClasses.tab}:hover:not([aria-selected="true"]):not(:disabled) {
  background: ${t.wash};
  color: ${t.text.primary};
}
.${tabClasses.row}[data-look="segmented"][data-depth] .${tabClasses.tab}[aria-selected="true"] {
  background: ${chosenTabLook.background};
  color: ${chosenTabLook.color};
  font-weight: ${chosenTabLook.fontWeight};
  box-shadow: ${chosenTabLook.boxShadow};
}
.${tabClasses.row}[data-look="segmented"][data-depth] .${tabClasses.tab}:focus-visible {
  outline: 2px solid ${t.focus};
  outline-offset: 1px;
}
.${tabClasses.row}[data-look="segmented"]:not([data-depth="0"]) {
  gap: ${CT.gap}px;
  padding: ${CT.trackPadding}px;
  width: var(--tc-compact-track-width, fit-content) !important;
  max-width: 100%;
}
.${tabClasses.row}[data-look="segmented"]:not([data-depth="0"]) .${tabClasses.tab} {
  flex: var(--tc-compact-wrap-segment-flex, 0 0 auto);
  min-height: var(--tc-compact-control-height, ${compactSpec.controlHeight}px);
  padding: 0 ${CT.paddingHorizontal}px;
  font-size: ${CT.fontSize}px;
  line-height: ${CT.lineHeight}px;
}

@media (prefers-reduced-motion: reduce) {
  .${tabClasses.tab} { transition: none; }
}
`;

/** Appends the tab stylesheet to <head> once per document. */
export const injectTabStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.head.querySelector('style[data-tc-tabs]')) return;
  const el = document.createElement('style');
  el.setAttribute('data-tc-tabs', 'true');
  el.textContent = tabStyles;
  document.head.appendChild(el);
};
